// Engine console: which routes are shown, by which API they call.
//
// The legacy admin API (admin-api.clairvos.com) used to accept calls only from the
// old admin hosts, so every page that uses it failed with "Failed to fetch" when
// served from engine.clairvos.com. The legacy API now allows engine.clairvos.com,
// so HIDE_LEGACY_PAGES is false and all pages are shown. The switch stays: set it
// back to true to hide those pages again (files stay in src/pages and
// src/components). That one change removes the sidebar entries, the routes, the
// alerts bell and the role check, and swaps /email-accounts for its Engine-only page.
//
// api values:
//   admin  - calls the legacy admin API (hidden while HIDE_LEGACY_PAGES is true)
//   engine - calls only the Engine API (VITE_ENGINE_API_URL)
//   none   - calls neither API
export const HIDE_LEGACY_PAGES = false

// Landing page: where / redirects and so the first screen after login. It is the
// Engine page, in both modes. The daily view is designed later.
export const LANDING_PATH = '/candidate-profiles/new'

// Every route in App.jsx must appear here (tests/legacy-pages.test.mjs checks it).
// Order matters only for matching: the first pattern that matches wins.
export const ROUTE_TABLE = [
  { path: '/', api: 'none', note: 'redirects to the landing page' },
  { path: '/dashboard', api: 'engine', file: 'pages/Dashboard.jsx' },
  // Engine screens that are not connected yet: one page file, one entry per route (config/engine-screens.js).
  { path: '/approvals/pulls', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/approvals/drafts', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/approvals/release', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/apply-jobs', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/lead-finder', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/leads', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/sequences', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/research', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/sending-control', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/do-not-contact', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/connections', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/accounts', api: 'none', file: 'pages/EngineScreen.jsx' },
  // Connected to the Engine access routes: pages/EngineScreen.jsx hands these two to pages/AuditLog.jsx and
  // pages/PeopleAccess.jsx, and keeps its not connected screen for a 404 or 503 answer.
  { path: '/audit-log', api: 'engine', file: 'pages/EngineScreen.jsx' },
  { path: '/people', api: 'engine', file: 'pages/EngineScreen.jsx' },
  { path: '/candidate-profiles/:profileId', api: 'engine', file: 'pages/CandidateProfile.jsx' },
  { path: '/outreach-campaigns/:campaignId', api: 'engine', file: 'pages/CandidateCampaign.jsx' },
  { path: '/business-campaigns', api: 'engine', file: 'pages/BusinessCampaign.jsx' },
  { path: '/business-campaigns/:campaignId', api: 'engine', file: 'pages/BusinessCampaign.jsx' },
  { path: '/clients', api: 'admin', file: 'pages/Clients.jsx' },
  { path: '/clients/:clientId', api: 'admin', file: 'pages/ClientDetail.jsx' },
  { path: '/campaigns', api: 'admin', file: 'pages/Campaigns.jsx' },
  { path: '/campaigns/upload', api: 'admin', file: 'pages/Campaigns.jsx' },
  { path: '/campaigns/:campaignId', api: 'admin', file: 'pages/CampaignDetail.jsx' },
  // Mixed page: the full page calls both APIs, so while legacy pages are hidden the
  // route shows the Engine-only page instead. Both files are kept.
  { path: '/email-accounts', api: 'engine', file: 'pages/EmailAccountsEngine.jsx', legacyFile: 'pages/EmailAccounts.jsx' },
  { path: '/blocklist', api: 'admin', file: 'pages/BlockList.jsx' },
  { path: '/prospects', api: 'admin', file: 'pages/Prospects.jsx' },
  { path: '/contracts', api: 'admin', file: 'pages/Contracts.jsx' },
  { path: '/prospects/:prospectId', api: 'admin', file: 'pages/ProspectDetail.jsx' },
  { path: '/industries', api: 'admin', file: 'pages/IndustriesPage.jsx' },
  { path: '/industries/:playbookId', api: 'admin', file: 'pages/IndustryDetail.jsx' },
  { path: '/industries/:playbookId/sub/:subIndustryId', api: 'admin', file: 'pages/SubIndustryDetail.jsx' },
  { path: '/playbooks', api: 'admin', note: 'old redirect to /industries' },
  { path: '/playbooks/:playbookId', api: 'admin', note: 'old redirect to /industries/:id' },
  { path: '/niche-briefs', api: 'admin', file: 'pages/NicheBriefs.jsx' },
  { path: '/niche-briefs/:nicheId', api: 'admin', file: 'pages/NicheBriefDetail.jsx' },
  { path: '/niche-briefs/:nicheId/templates', api: 'admin', file: 'pages/NicheTemplates.jsx' },
  { path: '/icp-templates', api: 'admin', note: 'old redirect to /industries' },
  { path: '/icp-templates/:id', api: 'admin', note: 'old redirect to /industries' },
  { path: '/outbound-pipeline', api: 'admin', note: 'old redirect to /niche-briefs' },
  { path: '/subject-lines', api: 'admin', note: 'old redirect to /industries' },
  { path: '/onboarding', api: 'admin', note: 'old redirect to /clients' },
  { path: '/support', api: 'admin', file: 'pages/Support.jsx' },
  { path: '/replies', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/legacy/replies', api: 'admin', file: 'pages/Replies.jsx' },
  { path: '/replies/unmatched', api: 'engine', file: 'pages/UnmatchedReplies.jsx' },
  { path: '/errors', api: 'admin', note: 'old redirect to /observability' },
  { path: '/queues', api: 'admin', note: 'old redirect to /observability' },
  { path: '/quarantine', api: 'admin', note: 'old redirect to /observability' },
  { path: '/observability', api: 'admin', file: 'pages/Observability.jsx' },
  { path: '/quality', api: 'admin', file: 'pages/Quality.jsx' },
  { path: '/live', api: 'admin', note: 'old redirect to /observability' },
  { path: '/reports', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/legacy/reports', api: 'admin', file: 'pages/Reports.jsx' },
  { path: '/kill-switch', api: 'admin', file: 'pages/KillSwitch.jsx' },
  { path: '/billing', api: 'admin', file: 'pages/Billing.jsx' },
  { path: '/feature-flags', api: 'admin', note: 'old redirect to /legacy/settings' },
  { path: '/portal', api: 'admin', file: 'pages/Portal.jsx' },
  { path: '/studio', api: 'admin', file: 'pages/Studio.jsx' },
  { path: '/templates', api: 'admin', file: 'pages/Templates.jsx' },
  { path: '/email-preview', api: 'none', note: 'old redirect to /email-accounts' },
  { path: '/analytics', api: 'admin', file: 'pages/Analytics.jsx' },
  { path: '/deliverability', api: 'none', note: 'old redirect to /email-accounts' },
  { path: '/compare', api: 'admin', note: 'old redirect to /analytics' },
  { path: '/costs', api: 'none', file: 'pages/EngineScreen.jsx' },
  { path: '/legacy/costs', api: 'admin', file: 'pages/Costs.jsx' },
  { path: '/cache', api: 'admin', note: 'old redirect to /observability' },
  { path: '/notifications', api: 'admin', note: 'old redirect to /legacy/settings' },
  { path: '/settings', api: 'none', file: 'pages/EngineSettings.jsx' },
  { path: '/legacy/settings', api: 'admin', file: 'pages/Settings.jsx' },
  { path: '/docs', api: 'none', file: 'pages/Docs.jsx' },
  { path: '*', api: 'none', file: 'pages/NotFound.jsx' },
]

function matches(pattern, pathname) {
  if (pattern === '*') return true
  const source = pattern.split('/').map(part => (part.startsWith(':') ? '[^/]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('/')
  return new RegExp(`^${source}/?$`).test(pathname)
}

export function routeFor(pathname, table = ROUTE_TABLE) {
  const path = pathname.split(/[?#]/)[0]
  return table.find(route => route.path !== '*' && matches(route.path, path)) || table.find(route => route.path === '*')
}

// True when the path is hidden because it calls the legacy admin API.
export function isHidden(pathname, hide = HIDE_LEGACY_PAGES, table = ROUTE_TABLE) {
  return hide && routeFor(pathname, table).api === 'admin'
}
